/**
 * Utility functions
 */
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Merge class names with tailwind-merge
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format currency
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format duration in seconds to human readable format
 */
export function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes < 60) {
    return `${minutes}m ${remainingSeconds.toFixed(0)}s`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  return `${hours}h ${remainingMinutes}m`;
}

/**
 * Format relative time
 */
export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) {
    return 'just now';
  } else if (diffMins < 60) {
    return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  } else if (diffDays < 7) {
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  } else {
    return date.toLocaleDateString();
  }
}

/**
 * Get status badge color — design tokens only (Agent 1 finding 2026-06-27).
 *
 * This used to return raw Tailwind pastels (bg-green-100, bg-amber-100…)
 * which leaked into every consumer and undid the per-tab token sweep.
 * Tokens map: completed→positive, processing→pillar-communication,
 * pending→warning, failed→negative, default→muted.
 */
export function getStatusColor(status: string): string {
  switch (status.toLowerCase()) {
    case 'completed':
      return 'bg-positive/15 text-positive';
    case 'processing':
      return 'bg-pillar-communication/15 text-pillar-communication';
    case 'pending':
      return 'bg-warning/15 text-warning';
    case 'failed':
      return 'bg-negative/15 text-negative';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

/**
 * Truncate text
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}

/**
 * ContentBlock[] → plain-string flattener.
 *
 * After the Phase 4 prompt update, narrative fields on challenge sections
 * (stimulus, quantitativeData, qualitativeSignals) and workshop participant
 * materials (scenarioBrief, roleCards.publicInfo/hiddenInfo/conflictingInfo)
 * ship as ContentBlock[] instead of plain strings. Admin review surfaces
 * still need a flat string — and React crashes if we hand it a raw
 * ContentBlock array as a child.
 *
 * Keep the flatten rules in sync with:
 *   • Flutter: ContentBlock.flattenToString in
 *     `playbook-mobile-flutter/lib/screens/micro_class/models/content_block.dart`
 *   • Web learner: proseFieldToPlain in
 *     `playbook-homepage/src/components/engines/EngineMediaCapture.tsx`
 *
 * Scope is intentionally lossy: paragraphs are joined by newlines, lists get
 * marker prefixes, key_value rows become "label: value" lines. Non-block
 * values pass through (strings as-is, others stringified).
 */
export function proseToPlain(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (!Array.isArray(v)) return String(v);
  const parts: string[] = [];
  for (const b of v) {
    if (!b || typeof b !== 'object') continue;
    const block = b as Record<string, unknown>;
    switch (block.type) {
      case 'paragraph':
        if (typeof block.text === 'string') parts.push(block.text);
        break;
      case 'ordered_list':
      case 'unordered_list': {
        const items = Array.isArray(block.items) ? block.items : [];
        items.forEach((it, i) => {
          if (typeof it !== 'string') return;
          const marker = block.type === 'ordered_list' ? `${i + 1}. ` : '• ';
          parts.push(marker + it);
        });
        break;
      }
      case 'key_value': {
        const items = Array.isArray(block.items) ? block.items : [];
        for (const kv of items) {
          if (!kv || typeof kv !== 'object') continue;
          const { label, value } = kv as Record<string, unknown>;
          if (typeof label === 'string' && typeof value === 'string') {
            parts.push(`${label}: ${value}`);
          }
        }
        break;
      }
    }
  }
  return parts.join('\n');
}
